/**
 * <MessageRow> — un message à sa place dans le fil (molecule).
 *
 * Elle pose la bulle du bon côté et lui donne ce qui l'entoure : qui parle, et sous la bulle la
 * ligne d'état — l'heure, « Envoi... », ou « Non envoyé » avec « Renvoyer ».
 *
 * Le message de l'élève est à droite, celui de l'autre à gauche : c'est la place, plus l'aplat,
 * qui dit qui parle. L'ancien fil de la messagerie alignait tout à gauche et ne les distinguait
 * que par la couleur.
 *
 * Les deux voix de l'autre (livre, chap. 07 : « l'agent en bulle, l'humain cité avec sa photo ») :
 *  - l'agent PARLE : sa tête est en bas, contre la queue de la dernière bulle de sa salve, et il
 *    n'est pas nommé — sa tête le dit. Quand l'agent est l'un des quatre (`champion`), c'est la
 *    ligne qui pose sa tête : `<ChampionAvatar>`, le disque et le nœud à sa couleur, la même
 *    forme que la capsule du tuteur. Jamais le champion en pied : à cette taille il ne se lit pas ;
 *  - l'humain est CITÉ : sa photo est en haut, à l'ouverture de la salve, avec son nom, et ce
 *    qu'il dit vient dessous en blocs, sans queue (ni liseré : retiré le 13/10).
 *
 * Un message qui n'est pas parti est MARQUÉ : il perd son aplat (`<MessageBubble face="unsent">`)
 * et sa ligne d'état propose de le renvoyer. L'ancien code le laissait dans le fil, identique aux
 * autres. Quand le serveur a dit pourquoi il le refuse (`failureReason`), la ligne le dit, comme
 * l'ancienne boîte « Erreur » ; un refus définitif (`retryable: false`) ne propose plus « Renvoyer ».
 *
 * Aucun accusé de lecture : l'élève n'en a pas aujourd'hui.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { type MessageVoiceControls } from "../MessageAttachment"
import { MessageBubble, type MessageBubbleFace, type UIMessageLink } from "../MessageBubble"
import {
    type UIConversationMessage,
    type UIMessageAuthor,
    type UIMessageStatus,
} from "../../../ports/primary/ui/types/studentConversation"

// Les formes d'un message vivent dans `ports/primary/ui/types` (remontées au câblage de la messagerie).
export type { UIConversationMessage, UIMessageAuthor, UIMessageStatus }

/** Comment l'autre parle : l'agent en bulle, l'humain cité. */
export type MessageSpeaker = "agent" | "human"

export type MessageRowProps = {
    message: UIConversationMessage
    /** Comment l'autre parle — sans effet sur un message de l'élève. */
    speaker: MessageSpeaker
    /** Premier message de sa salve : l'humain y est nommé, sa photo à côté. */
    head?: boolean
    /** Dernier message de sa salve : la bulle porte la queue, l'agent y pose sa tête. */
    tail?: boolean
    /**
     * L'agent est l'un des quatre : la ligne pose sa tête elle-même, à `SPEAKER_HEAD_SIZE`. Sans
     * effet sur un humain cité, qui garde sa capsule (`avatar`). Absent : `avatar`, comme avant.
     */
    champion?: ChampionName
    /** La capsule de l'humain (ou la tête d'un agent hors casting), `SPEAKER_AVATAR_SIZE` de côté au plus. */
    avatar?: React.ReactNode
    /** Le nom de l'humain cité — « Camille R. ». */
    authorName?: string
    /** La ligne sous la bulle d'un message parti : son heure, donnée par le fil à la fin d'une salve. */
    caption?: string
    /** « Renvoyer » un message en échec. Absent, l'échec est marqué sans geste. */
    onRetry?: () => void
    onOpenAttachment?: () => void
    voicePlaying?: boolean
    voiceProgress?: number
    onToggleVoice?: () => void
    /** Vocal chargé : le curseur et la vitesse. */
    voiceControls?: MessageVoiceControls
    /** Un lien du texte touché. Absent, le texte reste simple. */
    onPressLink?: (link: UIMessageLink) => void
    onLongPress?: () => void
    /** Un contenu libre dans la bulle, à la place du message — voir `<TypingIndicator>`. */
    children?: React.ReactNode
    testID?: string
}

/** La place de celui qui parle, à gauche de ses bulles : sa tête ou sa capsule y tient. */
export const SPEAKER_AVATAR_SIZE = 36

/** La tête d'un champion dans le fil : la plus petite taille du livre, celle de la capsule du tuteur. */
export const SPEAKER_HEAD_SIZE = 34

/** L'écart entre celui qui parle et sa bulle. */
const GUTTER_GAP = "2"

/** La part de la largeur du fil qu'un message occupe au plus : le vide de l'autre côté dit qui parle. */
export const MESSAGE_ROW_MAX_WIDTH = "88%"

const TEXTS = {
    sending: "Envoi...",
    failed: "Non envoyé",
    retry: "Renvoyer",
}

function faceOf(message: UIConversationMessage, speaker: MessageSpeaker): MessageBubbleFace {
    if (message.author === "other") return speaker
    return message.status === "failed" ? "unsent" : "own"
}

export function MessageRow(props: MessageRowProps) {
    const {
        message,
        speaker,
        head = false,
        tail = false,
        champion,
        avatar,
        authorName,
        caption,
        onRetry,
        onOpenAttachment,
        voicePlaying,
        voiceProgress,
        onToggleVoice,
        voiceControls,
        onPressLink,
        onLongPress,
        children,
        testID,
    } = props

    const own = message.author === "self"
    const status = own ? (message.status ?? "sent") : "sent"
    const cited = !own && speaker === "human"
    // L'agent pose sa tête contre la queue, en bas ; l'humain est présenté en haut, avant de parler.
    const showAvatar = !own && (cited ? head : tail)
    const speakerAvatar =
        champion != null && !cited ? <ChampionAvatar name={champion} size={SPEAKER_HEAD_SIZE} /> : avatar
    const showName = cited && head && authorName != null && authorName !== ""
    // Un texte riche a besoin d'une largeur donnée : sa ligne prend toute celle du fil.
    const stretched = !own && message.format === "rich"

    return (
        <View style={styles.root(own, stretched)} testID={testID}>
            {showName && (
                <Text size="text-sm" weight="bold" color="text-muted" style={styles.caption(true)}>
                    {authorName}
                </Text>
            )}

            <Row gap={GUTTER_GAP} align={cited ? "start" : "end"} style={styles.line}>
                {!own && <View style={styles.gutter}>{showAvatar ? speakerAvatar : null}</View>}
                <MessageBubble
                    face={faceOf(message, speaker)}
                    text={message.text}
                    format={message.format}
                    attachment={message.attachment}
                    tail={tail}
                    onOpenAttachment={onOpenAttachment}
                    voicePlaying={voicePlaying}
                    voiceProgress={voiceProgress}
                    onToggleVoice={onToggleVoice}
                    voiceControls={voiceControls}
                    onPressLink={onPressLink}
                    onLongPress={onLongPress}
                >
                    {children}
                </MessageBubble>
            </Row>

            {status === "sending" && (
                <Text size="text-sm" color="text-muted" style={styles.caption(false)}>
                    {TEXTS.sending}
                </Text>
            )}

            {status === "failed" && (
                <Row gap="1" align="center" justify="end" style={styles.failed}>
                    <Icon name="exclamation-circle" size={16} color="danger-strong" />
                    <Text size="text-md" weight="bold" color="danger-strong">
                        {TEXTS.failed}
                    </Text>
                    {onRetry != null && message.retryable !== false && (
                        // Marine, pas corail : à côté du rouge d'erreur, le corail de l'élève se lit
                        // comme une seconde alerte.
                        <Button label={TEXTS.retry} iconName="redo" variant="ghost" color="primary" onPress={onRetry} />
                    )}
                </Row>
            )}

            {status === "failed" && message.failureReason != null && message.failureReason !== "" && (
                <Text size="text-sm" color="danger-strong" style={styles.reason}>
                    {message.failureReason}
                </Text>
            )}

            {status === "sent" && caption != null && caption !== "" && (
                <Text size="text-sm" color="text-muted" style={styles.caption(!own)}>
                    {caption}
                </Text>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La bulle n'occupe jamais toute la ligne : le vide de l'autre côté dit qui parle. */
    root: (own: boolean, stretched: boolean) => ({
        maxWidth: MESSAGE_ROW_MAX_WIDTH,
        width: stretched ? MESSAGE_ROW_MAX_WIDTH : undefined,
        alignSelf: own ? ("flex-end" as const) : ("flex-start" as const),
        alignItems: own ? ("flex-end" as const) : ("flex-start" as const),
        gap: theme.spacing["1"],
    }),
    line: { maxWidth: "100%" },
    /** Une largeur, pas une hauteur : une bulle sans tête à côté d'elle garde la sienne. */
    gutter: {
        width: SPEAKER_AVATAR_SIZE,
        alignItems: "center",
    },
    /** Sous la bulle (ou au-dessus, pour le nom), pas sous la tête. */
    caption: (indented: boolean) => ({
        paddingHorizontal: theme.spacing["1"],
        marginLeft: indented ? SPEAKER_AVATAR_SIZE + theme.spacing[GUTTER_GAP] : 0,
    }),
    failed: { flexWrap: "wrap", paddingLeft: theme.spacing["1"] },
    /** Le motif du refus, sous « Non envoyé », calé à droite comme lui. */
    reason: { paddingHorizontal: theme.spacing["1"], textAlign: "right" },
}))
